// StepDelivery — step 3 of the wizard. Collects who the output is for and how
// much oversight to apply:
//   - audience   : one or more reader types; the pipeline blends the tone.
//   - cadence    : the "intervention budget" (minimal / standard / thorough).
//                  This is a knob for how many quality GATES run later in the
//                  pipeline — the more thorough, the more gates fire (see
//                  CADENCES below). Sent to the backend as intervention_budget.
//   - document owner / approver : names stamped into the requirements-doc
//                  header; REQUIRED once any compliance framework is flagged.
//   - Project Architecture : optional deployment fields (cloud, region,
//                  runtime, secrets, on-call, …). Each has an LLM "Suggest"
//                  button that ranks catalog options for this specific project.
import { useState } from 'react'
import { Send } from 'lucide-react'

import { api } from '@/lib/api'
import type { DeploymentCatalogResponse, RankedSuggestion } from '@/lib/api'
import { AUDIENCE_OPTIONS, type AudienceTag } from '@/components/intake/shapes'
import type { InterventionBudget } from '@/types'
import type { NewProjectForm, UpdateForm } from '../types'

// The three intervention-budget levels. The "G1 · G2 …" tags are the quality
// GATES that run in the pipeline: G1=first pass, G2/G3=deeper review passes,
// G4/G5=final checks. More gates = more scrutiny (and more time). `meta` is
// just the human label shown on each card.
const CADENCES: Array<{ id: InterventionBudget; title: string; meta: string; isDefault?: boolean }> = [
  { id: 'minimal', title: 'Minimal', meta: 'G1 · G4 · G5 — ship fast, low touch' },
  { id: 'standard', title: 'Standard', meta: 'G1 · G2 · G4 · G5 — balanced review', isDefault: true },
  { id: 'thorough', title: 'Thorough', meta: 'G1 · G2 · G3 · G4 · G5 — regulated work' },
]

// One deployment-architecture dropdown (cloud provider, region, etc.).
// Options come from the backend deployment `catalog`; the "Suggest" button
// asks the LLM to rank the best few for this project and floats them to the
// top. On the LLM call failing we silently keep the static catalog order.
function ArchSelect({
  fieldId,
  label,
  value,
  onChange,
  catalog,
  form,
}: {
  fieldId: string
  label: string
  value: string
  onChange: (next: string) => void
  catalog: DeploymentCatalogResponse | null
  form: NewProjectForm
}) {
  const [ranked, setRanked] = useState<RankedSuggestion[] | null>(null)
  const [loading, setLoading] = useState(false)
  const field = catalog?.fields.find((f) => f.field_id === fieldId)
  const baseOptions = (field?.entries ?? []).map((e) => ({ value: e.value, label: e.label }))
  const options = ranked
    ? [
        ...ranked.map((r) => ({ value: r.value, label: r.label })),
        ...baseOptions.filter((o) => !ranked.some((r) => r.value === o.value)),
      ]
    : baseOptions

  const suggest = async () => {
    setLoading(true)
    try {
      const resp = await api.suggestDeployment({
        field_id: fieldId,
        problem_statement: form.problem_statement,
        shape: form.shape,
        regulatory: form.regulatory,
        max_suggestions: 3,
      })
      setRanked(resp.suggestions)
      if (resp.suggestions.length > 0 && !value) onChange(resp.suggestions[0].value)
    } catch {
      // Keep the static catalog ordering on failure.
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <label className="form-field-label">
        {label}
        <button
          type="button"
          className="step-back"
          style={{ padding: 0, color: 'var(--x-red)', letterSpacing: 'normal', textTransform: 'none', fontFamily: 'var(--font-serif)', fontStyle: 'italic', fontSize: 11 }}
          onClick={suggest}
          disabled={loading}
        >
          {loading ? 'Thinking…' : '✦ Suggest for this project'}
        </button>
      </label>
      <select
        className="form-input with-icon"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Choose {label.toLowerCase()}…</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  )
}

export function StepDelivery({
  form,
  update,
  catalog,
}: {
  form: NewProjectForm
  update: UpdateForm
  catalog: DeploymentCatalogResponse | null
}) {
  const complianceFlagged = form.regulatory.some((r) => r && r !== 'none')

  const toggleAudience = (tag: AudienceTag) => {
    const has = form.audience.includes(tag)
    update({ audience: has ? form.audience.filter((a) => a !== tag) : [...form.audience, tag] })
  }

  const blend = form.audience
    .map((t) => AUDIENCE_OPTIONS.find((o) => o.id === t)?.label ?? t)
    .join(', ')

  return (
    <>
      <header className="step-header">
        <div className="step-section-tag">
          <Send size={14} />
          Delivery
        </div>
        <h1 className="step-h1">Who's <em>reading this</em>, and how involved do you want to be?</h1>
        <p className="step-lede">
          Pick any number of audiences — we blend the tone. Pick a review cadence that matches your{' '}
          <span className="accent">risk appetite</span>.
        </p>
      </header>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 32 }}>
        <div className="audience-panel">
          <div className="audience-head">Audience<span className="ms">· Multi-select</span></div>
          <div className="audience-chips">
            {AUDIENCE_OPTIONS.map((o) => (
              <button
                key={o.id}
                type="button"
                className={`audience-chip${form.audience.includes(o.id) ? ' selected' : ''}`}
                onClick={() => toggleAudience(o.id)}
              >
                {o.label}
              </button>
            ))}
          </div>
          <div className="audience-blend">
            Tone will blend: <b>{blend || 'none selected'}</b>
          </div>
        </div>

        <div className="audience-panel">
          <div className="audience-head">Review Cadence</div>
          <div className="cadence-stack">
            {CADENCES.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`cadence-card${form.cadence === c.id ? ' selected' : ''}`}
                onClick={() => update({ cadence: c.id })}
              >
                <div className="cadence-radio" />
                <div className="cadence-body">
                  <div className="cadence-title">
                    {c.title}
                    {c.isDefault && <span className="cadence-default">Default</span>}
                  </div>
                  <div className="cadence-meta">{c.meta}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <section className="form-card">
        <div className="form-card-head">
          <div className="form-card-title">
            Document Control
            {complianceFlagged && <span className="flag">required (compliance flag set)</span>}
          </div>
          <span className="form-card-aside">Lands in the requirements doc header</span>
        </div>
        <div className="form-card-grid">
          <div>
            <label className="form-field-label" htmlFor="npw-owner">
              Document Owner {complianceFlagged && <span className="req">*</span>}
            </label>
            <input
              id="npw-owner"
              className="form-input"
              type="text"
              placeholder="e.g. Jane Doe, Director of Engineering"
              value={form.document_owner}
              onChange={(e) => update({ document_owner: e.target.value })}
            />
          </div>
          <div>
            <label className="form-field-label" htmlFor="npw-approver">
              Document Approver {complianceFlagged && <span className="req">*</span>}
            </label>
            <input
              id="npw-approver"
              className="form-input"
              type="text"
              placeholder="e.g. John Smith, VP of Compliance"
              value={form.document_approver}
              onChange={(e) => update({ document_approver: e.target.value })}
            />
          </div>
        </div>
        <p className="form-helper">
          SOC 2 / ISO 27001 reviewers require a named owner and approver before treating the
          requirements package as <em>issued vs draft</em>.
        </p>
      </section>

      <section className="form-card">
        <div className="form-card-head">
          <div className="form-card-title">Project Architecture <span className="opt">/ optional</span></div>
          {/* PEG-DEPLOYMENT-MANIFEST-001 is a Phase-5 export-gate rule: the doc
              can't ship until the deployment manifest is filled. Answering
              these fields now "closes" that gate up front instead of later. */}
          <span className="form-card-aside">Closes PEG-DEPLOYMENT-MANIFEST-001 proactively</span>
        </div>
        <p className="form-card-desc">
          The Operations Architect agent reads these verbatim into the deployment manifest. Leave any
          field blank to defer the decision — the export gate will list missing keys for ratification
          at Phase 5.
        </p>
        <div className="form-card-grid">
          <ArchSelect
            fieldId="cloud_provider"
            label="Cloud Provider"
            value={form.cloud_provider}
            onChange={(v) => update({ cloud_provider: v })}
            catalog={catalog}
            form={form}
          />
          <ArchSelect
            fieldId="primary_region"
            label="Primary Region"
            value={form.primary_region}
            onChange={(v) => update({ primary_region: v })}
            catalog={catalog}
            form={form}
          />
          <ArchSelect
            fieldId="container_runtime"
            label="Container Runtime"
            value={form.container_runtime}
            onChange={(v) => update({ container_runtime: v })}
            catalog={catalog}
            form={form}
          />
          <ArchSelect
            fieldId="orchestrator"
            label="Orchestrator"
            value={form.orchestrator}
            onChange={(v) => update({ orchestrator: v })}
            catalog={catalog}
            form={form}
          />
          <ArchSelect
            fieldId="iac_framework"
            label="IaC Framework"
            value={form.iac_framework}
            onChange={(v) => update({ iac_framework: v })}
            catalog={catalog}
            form={form}
          />
          <ArchSelect
            fieldId="secrets_store"
            label="Secrets Store"
            value={form.secrets_store}
            onChange={(v) => update({ secrets_store: v })}
            catalog={catalog}
            form={form}
          />
          <ArchSelect
            fieldId="secrets_rotation_policy"
            label="Secrets Rotation Policy"
            value={form.secrets_rotation_policy}
            onChange={(v) => update({ secrets_rotation_policy: v })}
            catalog={catalog}
            form={form}
          />
          <ArchSelect
            fieldId="oncall_primary_rotation"
            label="On-call Primary Rotation"
            value={form.oncall_primary_rotation}
            onChange={(v) => update({ oncall_primary_rotation: v })}
            catalog={catalog}
            form={form}
          />
          <ArchSelect
            fieldId="oncall_runbook_index"
            label="On-call Runbook Index"
            value={form.oncall_runbook_index}
            onChange={(v) => update({ oncall_runbook_index: v })}
            catalog={catalog}
            form={form}
          />
          <ArchSelect
            fieldId="oncall_pager_severity_thresholds"
            label="Pager Severity Thresholds"
            value={form.oncall_pager_severity_thresholds}
            onChange={(v) => update({ oncall_pager_severity_thresholds: v })}
            catalog={catalog}
            form={form}
          />
        </div>
      </section>
    </>
  )
}
